import React from 'react'
import {action, observable, makeObservable, toJS} from 'mobx'
import {IMobxToJsConvertible} from '.'
import {definedContextValueHookFactory} from '@oc/hooks'

type EventHandler<T> = (payload: T) => Promise<unknown> | unknown

/**
 * Singleton class used to satisfy Typescript type requirements.
 * Enabled subscribing to event with `eventBus.channel<EventInterface>().on('eventName', eventHandler)`
 * instead of `eventBus.on<EventInterface, 'eventName>('eventName', eventHandler)` (which requires duplicate 'eventName').
 */
export class EventBusChannel<ChannelType> {
    constructor(private eventBus: EventBus) {
        makeObservable(this)
    }

    @action.bound
    public on<EventType extends keyof ChannelType & string>(
        eventName: EventType,
        eventHandler: EventHandler<ChannelType[EventType]>,
        order = 0,
        fireImmediately?: boolean
    ): () => void {
        return this.eventBus.on(eventName, eventHandler, order, fireImmediately)
    }

    @action.bound
    public off<EventType extends keyof ChannelType & string>(
        eventName: EventType,
        eventHandler: EventHandler<ChannelType[EventType]>,
        priority = 0
    ): void {
        this.eventBus.off(eventName, eventHandler, priority)
    }

    public listenerCount<EventType extends keyof ChannelType & string>(
        eventName: EventType,
        priority?: undefined
    ): number {
        return this.eventBus.listenerCount<ChannelType, EventType>(eventName, priority)
    }

    public trigger<EventType extends keyof ChannelType & string>(
        eventName: EventType,
        payload: ChannelType[EventType]
    ): Promise<void> {
        return this.eventBus.trigger(eventName, payload)
    }

    public useEvent<EventType extends keyof ChannelType & string>(
        eventName: EventType,
        eventHandler: EventHandler<ChannelType[EventType]> | null,
        priority = 0,
        fireImmediately?: boolean
    ): void {
        const bus = this.eventBus

        // eslint-disable-next-line react-hooks/rules-of-hooks
        React.useEffect(() => {
            if (eventHandler) {
                return bus.on(eventName, eventHandler, priority, fireImmediately)
            }
            return () => {}
        }, [bus, eventName, eventHandler, priority, fireImmediately])
    }
}

export class EventBus implements IMobxToJsConvertible<EventBus> {
    @observable private listeners = {} as {
        [eventName: string]: {
            [priority: number]: Set<EventHandler<any>>
        }
    }
    private lastEvents = {} as {
        [eventName: string]: any
    }
    private singletonChannel?: EventBusChannel<any>

    constructor() {
        makeObservable(this)
    }

    public toJS(): EventBus {
        return {
            ...toJS(this),
            channel: this.channel,
            trigger: this.trigger,
            on: this.on,
            off: this.off,
            listenerCount: this.listenerCount
        }
    }

    public channel<T>(): EventBusChannel<T> {
        let channel = this.singletonChannel
        if (!channel) {
            this.singletonChannel = channel = new EventBusChannel<T>(this)
        }
        return channel
    }

    private findListenerSet(eventName: string, priority: number): Set<EventHandler<any>> {
        if (!this.listeners[eventName]) {
            this.listeners[eventName] = {}
        }
        if (!this.listeners[eventName][priority]) {
            this.listeners[eventName][priority] = observable.set(new Set(), {deep: false})
        }
        return this.listeners[eventName][priority]
    }

    @action.bound
    public on<ChannelType, EventType extends keyof ChannelType & string>(
        eventName: EventType,
        eventHandler: EventHandler<ChannelType[EventType]>,
        priority = 0,
        fireImmediately?: boolean
    ): () => void {
        this.findListenerSet(eventName, priority).add(eventHandler)

        if (fireImmediately && this.lastEvents[eventName]) {
            eventHandler(this.lastEvents[eventName])
        }

        return () => this.off(eventName, eventHandler, priority)
    }

    @action.bound
    public off<ChannelType, EventType extends keyof ChannelType & string>(
        eventName: EventType,
        eventHandler: EventHandler<ChannelType[EventType]>,
        priority = 0
    ): void {
        this.findListenerSet(eventName, priority).delete(eventHandler)
    }

    public listenerCount<ChannelType, EventType extends keyof ChannelType & string>(
        eventName: EventType,
        priority?: number
    ): number {
        if (priority === undefined) {
            return Object.values(this.listeners[eventName] || {}).reduce((acc, cur) => acc + cur.size, 0)
        }
        return this.findListenerSet(eventName, priority).size
    }

    public async trigger<ChannelType, EventType extends keyof ChannelType & string>(
        eventName: EventType,
        payload: ChannelType[EventType]
    ): Promise<void> {
        this.lastEvents[eventName] = payload

        const priorities = Object.keys(this.listeners[eventName] || {})
            .map(priority => priority as unknown as number)
            .sort((priority1, priority2) => priority2 - priority1) // reversed order intended, trigger higher priority first

        for (const priority of priorities) {
            await Promise.all(Array.from(this.findListenerSet(eventName, priority)).map(listener => listener(payload)))
        }
    }
}

const EventBusContext = React.createContext<EventBus | null>(null)

export function EventBusContextProvider({children}: React.PropsWithChildren) {
    const [state] = React.useState(() => new EventBus())

    return <EventBusContext.Provider value={state}>{children}</EventBusContext.Provider>
}

export const useEventBus = definedContextValueHookFactory(EventBusContext, 'EventBusContext')

export function useEventBusChannel<ChannelType>(): EventBusChannel<ChannelType> {
    return useEventBus().channel<ChannelType>()
}

export function useEventBusEvent<ChannelType, EventType extends keyof ChannelType & string>(
    eventName: EventType,
    eventHandler: EventHandler<ChannelType[EventType]>,
    priority = 0,
    fireImmediately?: boolean
): void {
    useEventBusChannel<ChannelType>().useEvent(eventName, eventHandler, priority, fireImmediately)
}
